import { z } from 'zod';
import {
  EntityIdSchema,
  HhMmSchema,
  IsoDateSchema,
  IsoDateTimeSchema,
  PaginatedResponseSchema,
} from './common';

/**
 * Estados posibles de una justificación.
 */
export const JustificacionEstadoSchema = z.enum(['pendiente', 'aprobada', 'rechazada']);
export type JustificacionEstado = z.infer<typeof JustificacionEstadoSchema>;

/**
 * Justificación de inasistencia (ver docs/00-compartido/esquemas-datos.md §7).
 */
export const JustificacionSchema = z.object({
  id: EntityIdSchema,
  participante_id: EntityIdSchema,
  sesion_id: EntityIdSchema,
  motivo: z.string(),
  evidencia_url: z.string().url().nullable().optional(),
  solicitado_por_usuario_id: EntityIdSchema,
  estado: JustificacionEstadoSchema,
  aprobado_por_usuario_id: EntityIdSchema.nullable().optional(),
  aprobado_en: IsoDateTimeSchema.nullable().optional(),
  created_at: IsoDateTimeSchema,
});
export type Justificacion = z.infer<typeof JustificacionSchema>;

/**
 * Input para POST /api/v1/justificaciones.
 * Backend NO implementado aún (ver §Backend gaps del plan).
 */
export const JustificacionCreateInputSchema = z.object({
  participante_id: EntityIdSchema,
  sesion_id: EntityIdSchema,
  motivo: z.string().min(1),
  evidencia_url: z.string().url().optional(),
});
export type JustificacionCreateInput = z.infer<typeof JustificacionCreateInputSchema>;

/**
 * Input para PATCH /api/v1/justificaciones/:id (cambia estado).
 */
export const JustificacionUpdateInputSchema = z.object({
  estado: z.enum(['aprobada', 'rechazada']),
  comentario_admin: z.string().optional(),
});
export type JustificacionUpdateInput = z.infer<typeof JustificacionUpdateInputSchema>;

/**
 * Query params de GET /api/v1/justificaciones.
 * Todos opcionales: sin filtros devuelve todas (hasta el límite del backend).
 */
export const JustificacionListQuerySchema = z.object({
  cohorte_id: EntityIdSchema.optional(),
  participante_id: EntityIdSchema.optional(),
  estado: JustificacionEstadoSchema.optional(),
});
export type JustificacionListQuery = z.infer<typeof JustificacionListQuerySchema>;

/**
 * Justificación con datos denormalizados de participante y sesión.
 * Es lo que devuelve GET /api/v1/justificaciones para que el frontend
 * pueda mostrar nombres y fechas legibles sin resolver UUIDs.
 */
export const JustificacionConDetalleSchema = JustificacionSchema.extend({
  participante_nombre: z.string(),
  participante_apellido: z.string(),
  participante_cedula: z.string(),
  cohorte_id: EntityIdSchema,
  sesion_fecha: IsoDateSchema,
  sesion_hora_inicio: HhMmSchema,
  sesion_hora_fin: HhMmSchema,
  sesion_tema: z.string().nullable().optional(),
});
export type JustificacionConDetalle = z.infer<typeof JustificacionConDetalleSchema>;

/**
 * Response de GET /api/v1/justificaciones.
 */
export const JustificacionListResponseSchema = PaginatedResponseSchema(JustificacionConDetalleSchema);
export type JustificacionListResponse = z.infer<typeof JustificacionListResponseSchema>;